import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const DAYS = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun']
const DAY_ES = { Mon:'Lun', Tue:'Mar', Wed:'Mié', Thu:'Jue', Fri:'Vie', Sat:'Sáb', Sun:'Dom' }
const AV_COLORS = [
  {bg:'#fdeef3',fg:'#c03060'},{bg:'#e6f1fb',fg:'#0c447c'},
  {bg:'#e1f5ee',fg:'#085041'},{bg:'#eeedfe',fg:'#3c3489'},
  {bg:'#faece7',fg:'#4a1b0c'},{bg:'#faeeda',fg:'#854f0b'},
]

function initials(name) {
  return (name||'?').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()
}

const emptyStaff = {
  name:'', phone:'', email:'', role:'member',
  pay_rate:'', pay_type:'hourly', pay_type_v2:'driver_passenger',
  driver_rate:'', passenger_rate:'', mileage_rate:0.67,
  is_active:true, notes:'',
  availability:{ Mon:true, Tue:true, Wed:true, Thu:true, Fri:true, Sat:false, Sun:false },
  color:'#378ADD'
}

export default function Staff() {
  const [staff, setStaff] = useState([])
  const [selected, setSelected] = useState(null)
  const [form, setForm] = useState(emptyStaff)
  const [isNew, setIsNew] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [showAvailPrompt, setShowAvailPrompt] = useState(false)
  const [announcements, setAnnouncements] = useState([])
  const [newAnnouncement, setNewAnnouncement] = useState('')
  const [annLoading, setAnnLoading] = useState(true)

  useEffect(() => { fetchStaff(); fetchAnnouncements() }, [])

  async function fetchAnnouncements() {
    const { data } = await supabase.from('staff_announcements').select('*').order('created_at', { ascending: false })
    setAnnouncements(data||[])
    setAnnLoading(false)
  }

  async function addAnnouncement() {
    if (!newAnnouncement.trim()) return
    const { data } = await supabase.from('staff_announcements').insert([{
      body: newAnnouncement.trim(),
      created_at: new Date().toISOString()
    }]).select().single()
    if (data) setAnnouncements(prev=>[data,...prev])
    setNewAnnouncement('')
  }

  async function deleteAnnouncement(id) {
    await supabase.from('staff_announcements').delete().eq('id', id)
    setAnnouncements(prev=>prev.filter(a=>a.id!==id))
  }

  // Show Sunday prompt to confirm next week availability
  useEffect(() => {
    const today = new Date()
    const isSunday = today.getDay() === 0
    const lastPrompt = localStorage.getItem('avail_prompt_date')
    const todayStr = today.toISOString().slice(0,10)
    if (isSunday && lastPrompt !== todayStr) {
      setShowAvailPrompt(true)
    }
  }, [])

  async function fetchStaff() {
    setLoading(true)
    const { data } = await supabase.from('staff').select('*').order('name')
    setStaff(data || [])
    setLoading(false)
  }

  function selectStaff(s) {
    setSelected(s.id)
    setForm({ ...emptyStaff, ...s, availability: s.availability || emptyStaff.availability })
    setIsNew(false)
  }

  function newStaff() {
    setSelected('new')
    setForm({ ...emptyStaff })
    setIsNew(true)
  }

  async function save() {
    setSaving(true)
    const payload = {
      name: form.name,
      phone: form.phone || '',
      email: form.email || '',
      role: form.role || 'member',
      pay_rate: parseFloat(form.pay_rate) || null,
      pay_type: form.pay_type || 'hourly',
      pay_type_v2: form.pay_type_v2 || 'driver_passenger',
      driver_rate: parseFloat(form.driver_rate) || null,
      passenger_rate: parseFloat(form.passenger_rate) || null,
      mileage_rate: parseFloat(form.mileage_rate) || 0.67,
      is_active: form.is_active,
      notes: form.notes || '',
      availability: form.availability,
      color: form.color || '#378ADD',
      pin: form.pin || null,
    }
    if (isNew) {
      const { data, error } = await supabase.from('staff').insert([payload]).select().single()
      if (!error && data) {
        setStaff(prev => [...prev, data].sort((a,b) => a.name.localeCompare(b.name)))
        setSelected(data.id)
        setIsNew(false)
      } else { console.error('Insert error:', error) }
    } else {
      const { data, error } = await supabase.from('staff').update(payload).eq('id', selected).select().single()
      if (!error && data) setStaff(prev => prev.map(s => s.id === selected ? data : s))
      else { console.error('Update error:', error) }
    }
    setSaving(false)
  }

  async function deleteStaff() {
    if (!window.confirm('¿Eliminar este miembro del personal?')) return
    await supabase.from('staff').delete().eq('id', selected)
    setStaff(prev => prev.filter(s => s.id !== selected))
    setSelected(null)
  }

  function toggleDay(day) {
    setForm(f => ({ ...f, availability: { ...f.availability, [day]: !f.availability[day] } }))
  }

  const selectedStaff = staff.find(s => s.id === selected)

  return (
    <div style={s.wrap}>
      {/* Sunday availability prompt */}
      {showAvailPrompt && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:200}}>
          <div style={{background:'#fff',borderRadius:16,padding:'24px 28px',width:380,boxShadow:'0 8px 40px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:20,marginBottom:8}}>📅 ¡Es domingo!</div>
            <div style={{fontSize:15,fontWeight:600,color:'#202124',marginBottom:8}}>Confirmar disponibilidad de la semana</div>
            <div style={{fontSize:13,color:'#5f6368',marginBottom:16,lineHeight:1.6}}>
              La disponibilidad de la semana pasada se ha copiado automáticamente para esta semana. ¿Hay cambios para alguna empleada?
            </div>
            <div style={{display:'flex',flexDirection:'column',gap:8,marginBottom:16}}>
              {staff.map(s=>(
                <div key={s.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'#f8f9fa',borderRadius:8}}>
                  <div style={{fontSize:13,fontWeight:500,flex:1}}>{s.name}</div>
                  {['Lun','Mar','Mié','Jue','Vie','Sáb'].map((day,i)=>{
                    const key = ['Mon','Tue','Wed','Thu','Fri','Sat'][i]
                    const avail = s.availability?.[key] !== false
                    return (
                      <button key={day} style={{fontSize:9,padding:'2px 5px',borderRadius:4,border:'1px solid',cursor:'pointer',
                        background:avail?'#e8547a':'#fff',color:avail?'#fff':'#bdc1c6',borderColor:avail?'#e8547a':'#eee'}}
                        onClick={async()=>{
                          const newAvail = {...(s.availability||{}), [key]: !avail}
                          await supabase.from('staff').update({availability:newAvail}).eq('id',s.id)
                          setStaff(prev=>prev.map(x=>x.id===s.id?{...x,availability:newAvail}:x))
                        }}>
                        {day}
                      </button>
                    )
                  })}
                </div>
              ))}
            </div>
            <button style={{width:'100%',padding:'10px',background:'#e8547a',color:'#fff',border:'none',borderRadius:10,fontWeight:600,cursor:'pointer',fontSize:14}}
              onClick={()=>{
                localStorage.setItem('avail_prompt_date', new Date().toISOString().slice(0,10))
                setShowAvailPrompt(false)
              }}>
              ✓ Confirmado — empezar la semana
            </button>
          </div>
        </div>
      )}
      {/* LIST */}
      <div style={s.list}>
        <div style={s.listHead}>
          <div style={s.listTitle}>Personal</div>
          <div style={s.listSub}>{staff.filter(s=>s.is_active).length} activos</div>
        </div>
        <div style={s.listScroll}>
          {loading ? <div style={s.loading}>Cargando...</div> :
            staff.length === 0 ? <div style={s.loading}>Sin personal aún</div> :
            staff.map((st, i) => {
              const av = AV_COLORS[i % AV_COLORS.length]
              return (
                <div key={st.id} style={{...s.row, ...(selected===st.id?s.rowActive:{})}}
                  onClick={() => selectStaff(st)}>
                  <div style={{...s.av, background:av.bg, color:av.fg}}>{initials(st.name)}</div>
                  <div style={s.rowInfo}>
                    <div style={s.rowName}>{st.name}</div>
                    <div style={s.rowSub}>{st.role === 'lead' ? 'Líder de equipo' : 'Miembro'}</div>
                  </div>
                  <div style={{display:'flex', flexDirection:'column', alignItems:'flex-end', gap:3}}>
                    {st.role === 'lead' && <span style={s.leadBadge}>L</span>}
                    {!st.is_active && <span style={s.inactiveBadge}>Inactivo</span>}
                  </div>
                </div>
              )
            })
          }
        </div>
        <button style={s.addBtn} onClick={newStaff}>+ Agregar personal</button>
      </div>

      {/* ANNOUNCEMENTS PANEL */}
      <div style={{padding:'10px 12px',borderBottom:'1px solid #e3e5e8',background:'#f8f9fa'}}>
        <div style={{fontSize:11,fontWeight:600,color:'#c03060',textTransform:'uppercase',letterSpacing:'.05em',marginBottom:6}}>📢 Avisos al equipo</div>
        <div style={{display:'flex',gap:4,marginBottom:8}}>
          <input style={{flex:1,fontSize:11,padding:'5px 8px',border:'1px solid #e3e5e8',borderRadius:7,background:'#fff'}}
            value={newAnnouncement} placeholder="Escribe un aviso..."
            onChange={e=>setNewAnnouncement(e.target.value)}
            onKeyDown={e=>e.key==='Enter'&&addAnnouncement()}/>
          <button style={{fontSize:11,padding:'4px 8px',border:'none',borderRadius:7,background:'#e8547a',color:'#fff',cursor:'pointer'}}
            onClick={addAnnouncement}>+</button>
        </div>
        {announcements.slice(0,3).map(a=>(
          <div key={a.id} style={{display:'flex',alignItems:'flex-start',gap:4,padding:'5px 0',borderBottom:'1px solid #e3e5e8'}}>
            <div style={{flex:1,fontSize:11,color:'#333',lineHeight:1.4}}>{a.body}</div>
            <button style={{fontSize:12,border:'none',background:'none',color:'#ddd',cursor:'pointer',flexShrink:0}} onClick={()=>deleteAnnouncement(a.id)}>×</button>
          </div>
        ))}
      </div>

      {/* DETAIL */}
      <div style={s.detail}>
        {!selected ? (
          <div style={s.empty}>
            <div style={{fontSize:40}}>👩</div>
            <div style={{fontSize:14, color:'#80868b', marginTop:8}}>Selecciona un miembro</div>
            <div style={{fontSize:12, color:'#9aa0a6', marginTop:4}}>o agrega uno nuevo</div>
          </div>
        ) : (
          <div style={s.profile}>
            {/* Header */}
            <div style={s.profHead}>
              <div style={{...s.profAv,
                background: AV_COLORS[(staff.findIndex(s=>s.id===selected)||0) % AV_COLORS.length]?.bg || '#fdeef3',
                color: AV_COLORS[(staff.findIndex(s=>s.id===selected)||0) % AV_COLORS.length]?.fg || '#c03060'
              }}>
                {initials(form.name||'NC')}
              </div>
              <div style={{flex:1}}>
                <input style={s.nameInput} value={form.name}
                  onChange={e => setForm(f=>({...f, name:e.target.value}))}
                  placeholder="Nombre completo" />
                <div style={{fontSize:12, color:'#80868b', marginTop:2}}>
                  {form.role === 'lead' ? '⭐ Líder de equipo' : 'Miembro del equipo'}
                </div>
              </div>
              <div style={s.actions}>
                {!isNew && <button style={s.delBtn} onClick={deleteStaff}>Eliminar</button>}
                <button style={s.saveBtn} onClick={save} disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </button>
              </div>
            </div>

            <div style={s.body}>
              {/* Role & Status */}
              <Section title="Rol y estado">
                <div style={{display:'flex', gap:12, flexWrap:'wrap'}}>
                  <div style={{flex:1}}>
                    <div style={s.fieldLabel}>Rol</div>
                    <select style={s.input} value={form.role}
                      onChange={e => setForm(f=>({...f, role:e.target.value}))}>
                      <option value="member">Miembro del equipo</option>
                      <option value="lead">Líder de equipo</option>
                    </select>
                  </div>
                  <div style={{flex:1}}>
                    <div style={s.fieldLabel}>Estado</div>
                    <select style={s.input} value={form.is_active ? 'active' : 'inactive'}
                      onChange={e => setForm(f=>({...f, is_active:e.target.value==='active'}))}>
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                    </select>
                  </div>
                </div>
              </Section>

              {/* Contact */}
              <Section title="Contacto">
                <div style={s.grid}>
                  <div>
                    <div style={s.fieldLabel}>Teléfono</div>
                    <input style={s.input} value={form.phone||''} placeholder="(310) 555-0000"
                      onChange={e=>setForm(f=>({...f,phone:e.target.value}))} />
                  </div>
                  <div>
                    <div style={s.fieldLabel}>Correo</div>
                    <input style={s.input} value={form.email||''} placeholder="correo@email.com"
                      onChange={e=>setForm(f=>({...f,email:e.target.value}))} />
                  </div>
                  <div>
                    <div style={s.fieldLabel}>PIN de acceso (6 dígitos)</div>
                    <input style={s.input} value={form.pin||''} placeholder="123456" maxLength={6}
                      type="text" inputMode="numeric"
                      onChange={e=>setForm(f=>({...f,pin:e.target.value.replace(/\D/g,'').slice(0,6)}))} />
                    <div style={{fontSize:10,color:'#9aa0a6',marginTop:2}}>El equipo usa esto para entrar a su lista de rutas</div>
                    <button type="button" style={{fontSize:10,marginTop:6,padding:'3px 8px',border:'1px solid #e3e5e8',borderRadius:6,background:'#fff',color:'#80868b',cursor:'pointer'}}
                      onClick={()=>{
                        const newPin = Array.from({length:6},()=>Math.floor(Math.random()*10)).join('')
                        setForm(f=>({...f,pin:newPin}))
                        alert(`Nuevo PIN: ${newPin}\nGuarda el perfil para aplicarlo.`)
                      }}>🔄 Generar PIN nuevo</button>
                  </div>
                  <div>
                    <div style={s.fieldLabel}>Enlace del portal</div>
                    <div style={{display:'flex',gap:6}}>
                      <input style={{...s.input,flex:1,color:'#5f6368'}} value="kittyscleaning.netlify.app/staff" readOnly />
                      <button type="button" style={{fontSize:11,padding:'0 12px',border:'1px solid #e3e5e8',borderRadius:8,background:'#f8f9fa',color:'#e8547a',cursor:'pointer',fontWeight:600,whiteSpace:'nowrap'}}
                        onClick={()=>{
                          navigator.clipboard.writeText('https://kittyscleaning.netlify.app/staff')
                          alert('¡Enlace copiado!')
                        }}>📋 Copiar</button>
                    </div>
                    <div style={{fontSize:10,color:'#9aa0a6',marginTop:2}}>Mismo enlace para todo el equipo — cada quien entra con su PIN</div>
                  </div>
                </div>
              </Section>

              {/* Pay */}
              <Section title="Pago">
                <div style={s.grid}>
                  <div style={{gridColumn:'1/-1'}}>
                    <div style={s.fieldLabel}>Tipo de pago</div>
                    <select style={s.input} value={form.pay_type_v2||'driver_passenger'}
                      onChange={e=>setForm(f=>({...f,pay_type_v2:e.target.value}))}>
                      <option value="driver_passenger">Conductora / Pasajera (tarifa diferente)</option>
                      <option value="base_mileage">Base + Millas</option>
                    </select>
                  </div>
                </div>

                {(form.pay_type_v2||'driver_passenger') === 'driver_passenger' && (
                  <div style={s.grid}>
                    <div>
                      <div style={s.fieldLabel}>Tarifa conductora ($/hr)</div>
                      <input style={s.input} type="number" min="0" step="0.50"
                        value={form.driver_rate||''} placeholder="20.00"
                        onChange={e=>setForm(f=>({...f,driver_rate:e.target.value}))}/>
                    </div>
                    <div>
                      <div style={s.fieldLabel}>Tarifa pasajera ($/hr)</div>
                      <input style={s.input} type="number" min="0" step="0.50"
                        value={form.passenger_rate||''} placeholder="18.00"
                        onChange={e=>setForm(f=>({...f,passenger_rate:e.target.value}))}/>
                    </div>
                    {(form.driver_rate||form.passenger_rate) && (
                      <div style={{...s.payPreview,gridColumn:'1/-1'}}>
                        Conductora: ${form.driver_rate||0}/hr · Pasajera: ${form.passenger_rate||0}/hr
                      </div>
                    )}
                  </div>
                )}

                {form.pay_type_v2 === 'base_mileage' && (
                  <div style={s.grid}>
                    <div>
                      <div style={s.fieldLabel}>Tarifa base ($/hr)</div>
                      <input style={s.input} type="number" min="0" step="0.50"
                        value={form.pay_rate||''} placeholder="18.00"
                        onChange={e=>setForm(f=>({...f,pay_rate:e.target.value}))}/>
                    </div>
                    <div>
                      <div style={s.fieldLabel}>Tarifa por milla ($/mi)</div>
                      <input style={s.input} type="number" min="0" step="0.01"
                        value={form.mileage_rate||0.67} placeholder="0.67"
                        onChange={e=>setForm(f=>({...f,mileage_rate:e.target.value}))}/>
                    </div>
                    <div style={{...s.payPreview,gridColumn:'1/-1'}}>
                      Base: ${form.pay_rate||0}/hr + ${form.mileage_rate||0.67}/mi · Tasa CA 2026: $0.67/mi
                    </div>
                  </div>
                )}
              </Section>

              {/* Availability */}
              <Section title="Disponibilidad">
                <div style={s.availRow}>
                  {DAYS.map(day => (
                    <div key={day}
                      style={{...s.dayBtn, ...(form.availability?.[day] ? s.dayOn : s.dayOff)}}
                      onClick={() => toggleDay(day)}>
                      {DAY_ES[day]}
                    </div>
                  ))}
                </div>
                <div style={{fontSize:11, color:'#9aa0a6', marginTop:6}}>
                  Disponible: {DAYS.filter(d=>form.availability?.[d]).map(d=>DAY_ES[d]).join(', ') || 'Ningún día seleccionado'}
                </div>
              </Section>

              {/* Notes */}
              <Section title="Notas internas">
                <textarea style={s.textarea} value={form.notes||''} rows={3}
                  placeholder="Notas sobre este miembro del personal..."
                  onChange={e=>setForm(f=>({...f,notes:e.target.value}))} />
              </Section>

              {/* Stats (for existing staff) */}
              {!isNew && selectedStaff && (
                <Section title="Resumen">
                  <div style={s.statsRow}>
                    <div style={s.statCard}>
                      <div style={s.statNum}>—</div>
                      <div style={s.statLbl}>Limpiezas este mes</div>
                    </div>
                    <div style={s.statCard}>
                      <div style={s.statNum}>—</div>
                      <div style={s.statLbl}>Horas este mes</div>
                    </div>
                    <div style={s.statCard}>
                      <div style={s.statNum}>—</div>
                      <div style={s.statLbl}>Pago este período</div>
                    </div>
                  </div>
                  <div style={{fontSize:11,color:'#9aa0a6',marginTop:6}}>
                    Los datos de nómina se calcularán cuando agreguemos el módulo de nómina.
                  </div>
                </Section>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontSize:10, fontWeight:600, textTransform:'uppercase', letterSpacing:'.06em', color:'#9aa0a6', marginBottom:8 }}>{title}</div>
      {children}
    </div>
  )
}

const s = {
  wrap:{ display:'flex', height:'100%', overflow:'hidden' },
  list:{ width:210, borderRight:'1px solid #e3e5e8', display:'flex', flexDirection:'column', background:'#f8f9fa', flexShrink:0 },
  listHead:{ padding:'14px 14px 10px', borderBottom:'1px solid #e3e5e8' },
  listTitle:{ fontSize:15, fontWeight:600, color:'#202124' },
  listSub:{ fontSize:11, color:'#9aa0a6', marginTop:2 },
  listScroll:{ flex:1, overflowY:'auto', padding:6 },
  loading:{ padding:16, fontSize:12, color:'#80868b', textAlign:'center' },
  row:{ display:'flex', alignItems:'center', gap:8, padding:'8px 8px', borderRadius:8, cursor:'pointer', marginBottom:2 },
  rowActive:{ background:'#fff', borderLeft:'3px solid #e8547a', paddingLeft:5 },
  av:{ width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:600, flexShrink:0 },
  rowInfo:{ flex:1, minWidth:0 },
  rowName:{ fontSize:12, fontWeight:500, color:'#202124' },
  rowSub:{ fontSize:10, color:'#80868b', marginTop:1 },
  leadBadge:{ fontSize:9, background:'#fdeef3', color:'#c03060', padding:'1px 5px', borderRadius:3, fontWeight:700 },
  inactiveBadge:{ fontSize:9, background:'#e3e5e8', color:'#80868b', padding:'1px 5px', borderRadius:3 },
  addBtn:{ margin:8, padding:'8px', border:'1px dashed #dadce0', borderRadius:8, background:'none', fontSize:12, color:'#e8547a', fontWeight:500, cursor:'pointer' },
  detail:{ flex:1, overflow:'hidden', display:'flex', flexDirection:'column' },
  empty:{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', flexDirection:'column' },
  profile:{ flex:1, display:'flex', flexDirection:'column', overflow:'hidden' },
  profHead:{ display:'flex', alignItems:'center', gap:12, padding:'14px 18px', borderBottom:'1px solid #e3e5e8', background:'#fff', flexShrink:0 },
  profAv:{ width:48, height:48, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:18, fontWeight:600, flexShrink:0 },
  nameInput:{ fontSize:18, fontWeight:600, border:'none', outline:'none', background:'transparent', color:'#202124', width:'100%' },
  actions:{ display:'flex', gap:6 },
  delBtn:{ fontSize:12, padding:'6px 12px', border:'1px solid #e3e5e8', borderRadius:8, background:'#fff', color:'#80868b', cursor:'pointer' },
  saveBtn:{ fontSize:12, padding:'6px 14px', border:'none', borderRadius:8, background:'#e8547a', color:'#fff', fontWeight:500, cursor:'pointer' },
  body:{ flex:1, overflowY:'auto', padding:'16px 18px' },
  grid:{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8 },
  fieldLabel:{ fontSize:10, color:'#80868b', marginBottom:3 },
  input:{ width:'100%', padding:'7px 9px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12, background:'#f8f9fa', color:'#202124' },
  textarea:{ width:'100%', padding:'8px 10px', border:'1px solid #e3e5e8', borderRadius:8, fontSize:12, background:'#f8f9fa', color:'#202124', resize:'vertical' },
  availRow:{ display:'flex', gap:6, flexWrap:'wrap' },
  dayBtn:{ padding:'6px 10px', borderRadius:8, fontSize:12, fontWeight:500, cursor:'pointer', border:'1.5px solid transparent', userSelect:'none' },
  dayOn:{ background:'#e8547a', color:'#fff', borderColor:'#e8547a' },
  dayOff:{ background:'#fff', color:'#9aa0a6', borderColor:'#e3e5e8' },
  payPreview:{ fontSize:11, color:'#1d9e75', background:'#e1f5ee', padding:'5px 9px', borderRadius:6, marginTop:6 },
  statsRow:{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 },
  statCard:{ background:'#f8f9fa', borderRadius:8, padding:'10px 12px', textAlign:'center' },
  statNum:{ fontSize:20, fontWeight:600, color:'#202124' },
  statLbl:{ fontSize:10, color:'#80868b', marginTop:3 },
}
